// Google Analytics 4 (gtag.js). Requires VITE_GA_MEASUREMENT_ID; without it,
// everything here quietly no-ops so the app works before analytics is
// configured (e.g. local dev, which intentionally has no ID set so test
// traffic doesn't pollute production data).

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let _loaded = false;
let _area = null;

// Standard geohash encoding. Precision 5 is a ~4.9km × 4.9km cell — coarse
// enough that events carry a neighborhood, never a pinpoint location.
const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
function geohash(lat, lon, precision = 5) {
  let latLo = -90, latHi = 90, lonLo = -180, lonHi = 180;
  let hash = '', bits = 0, ch = 0, evenBit = true;
  while (hash.length < precision) {
    if (evenBit) {
      const mid = (lonLo + lonHi) / 2;
      if (lon >= mid) { ch = ch * 2 + 1; lonLo = mid; } else { ch = ch * 2; lonHi = mid; }
    } else {
      const mid = (latLo + latHi) / 2;
      if (lat >= mid) { ch = ch * 2 + 1; latLo = mid; } else { ch = ch * 2; latHi = mid; }
    }
    evenBit = !evenBit;
    if (++bits === 5) { hash += GEOHASH_BASE32[ch]; bits = 0; ch = 0; }
  }
  return hash;
}

// Sets the coarse area attached to every subsequent event, from the user's
// active home base (predefined city or custom pin).
export function setAnalyticsArea(lat, lon) {
  _area = (lat != null && lon != null) ? geohash(lat, lon) : null;
}

// Stable place identity for aggregating events per venue. The API re-encrypts
// place ids on every response, so `activity.id` never matches across fetches —
// name + coordinates does. Kept under GA4's 100-char param value limit.
export function placeKey(a) {
  if (!a) return undefined;
  const coords = a.lat != null ? `@${a.lat.toFixed(4)},${a.lon.toFixed(4)}` : '';
  return `${(a.name || '').slice(0, 70)}${coords}`;
}

// Loads gtag.js and configures it with pageviews off — the SPA sends its own
// page_view events on route change instead (see trackPageview), since the
// initial gtag('config') call fires before the router has settled on a path.
export function initAnalytics() {
  if (!GA_ID || _loaded || typeof document === 'undefined') return;
  _loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

export function trackPageview(path) {
  if (!GA_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', { page_path: path });
}

export function trackEvent(name, params = {}) {
  if (!GA_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', name, _area ? { area: _area, ...params } : params);
}
